import { useQueries, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import {
  getAssetOutcomes,
  getBands,
  getCohort,
  getFormatOptions,
  getImprovementAreas,
  getRunOptions,
  getVoxShare,
  getWeekly,
  messageFor,
  statsKey,
  type AssetOutcomes,
  type BandsData,
  type Cohort,
  type CohortRow,
} from "./api";
import { OPERATOR_GAP, PerformanceFilters, useControls } from "./controls";
import { bandFor, formatCount, formatScore, performanceTiles, statsQuery } from "./format";

const TELEMETRY = "CVCE telemetry";

function SectionError({ title, error, areaName }: { title: string; error: unknown; areaName: string }) {
  return (
    <Alert>
      <AlertTitle>{title} unavailable</AlertTitle>
      <AlertDescription>{messageFor(error, areaName, TELEMETRY)}</AlertDescription>
    </Alert>
  );
}

function Tiles({ outcomes, q }: { outcomes: AssetOutcomes; q: string }) {
  const vox = useQuery({ queryKey: statsKey("vox-share", q), queryFn: () => getVoxShare(q) });
  const overall = vox.data?.find((r) => r.scope === "overall") ?? null;
  const tiles = performanceTiles(outcomes, overall);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {tiles.map((tile) => (
        <Card key={tile.key} className={tile.decider ? "border-ks-purple" : undefined}>
          <CardContent className="flex flex-col gap-1 py-3">
            <p className="text-sm font-semibold text-ks-ink">
              {tile.label}
              {tile.decider && <span className="font-normal"> · the decider</span>}
            </p>
            <p className="text-2xl font-bold text-ks-ink tabular-nums">
              {tile.key === "vox_share" && vox.isPending ? "…" : tile.value}
            </p>
            <p className="text-xs text-ks-ink">
              {tile.key === "vox_share" && vox.isError ? "VOX share could not be read from CVCE telemetry." : tile.detail}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function WeeklyEngineChart({ q, areaName }: { q: string; areaName: string }) {
  const weekly = useQuery({ queryKey: statsKey("weekly", q), queryFn: () => getWeekly(q) });
  if (weekly.isError) return <SectionError title="Weekly scores" error={weekly.error} areaName={areaName} />;
  const data = (weekly.data ?? []).map((w) => ({ week: w.week_start, score: w.avg_engine_score }));
  return (
    <ChartFrame
      title="Weekly Avg Marketing Engine Score"
      subtitle="By asset week · 1-10 AI rubric. A week with no rated asset is a gap, not a zero."
      titleVisible
      pending={weekly.isPending}
      pointCount={data.length}
      emptyMessage="No rated assets in this range."
    >
      <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" />
        <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" width={40} />
        <Tooltip
          contentStyle={{
            background: "var(--color-ks-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-ks-sm)",
            fontSize: 12,
          }}
        />
        <Line
          type="linear"
          dataKey="score"
          name="Avg Marketing Engine Score"
          stroke="var(--color-ks-purple)"
          strokeWidth={2}
          dot={{ r: 3, fill: "var(--color-ks-purple)" }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartFrame>
  );
}

const DIMENSIONS: { key: keyof AssetOutcomes; band: string; label: string }[] = [
  { key: "avg_relate", band: "relate", label: "Relate" },
  { key: "avg_champion_truth", band: "champion_truth", label: "Champion Truth" },
  { key: "avg_empower", band: "empower", label: "Empower" },
  { key: "avg_brand_voice", band: "brand_voice", label: "Brand Voice" },
  { key: "avg_engagement", band: "engagement", label: "Engagement" },
];

function AssetAssessment({ outcomes }: { outcomes: AssetOutcomes }) {
  const bands = useQuery({ queryKey: ["cvce_marketing", "bands"], queryFn: getBands });
  const sets: BandsData = bands.data?.data ?? {};
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Asset Assessment</CardTitle>
        <p className="text-sm text-ks-ink">Rates each whole asset, every section included, on a 0-2 scale (the mean across its rated sections).</p>
      </CardHeader>
      <CardContent>
        {outcomes.rated === 0 ? (
          <p className="text-sm text-ks-ink">No rated assets yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ks-grey-4">
            {DIMENSIONS.map((d) => {
              const value = outcomes[d.key] as number | null;
              const set = sets[d.band];
              const band = set ? bandFor(value, set.bands) : null;
              return (
                <li key={d.key} className="flex items-center justify-between gap-2 py-2 text-sm text-ks-ink">
                  <span>{d.label}</span>
                  <span className="flex items-center gap-2 tabular-nums">
                    {formatScore(value, 2)} / 2
                    {band ? (
                      <Badge variant="outline">{band}</Badge>
                    ) : (
                      <span className="text-xs">{set || bands.isPending ? "" : "no target band set"}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {bands.isError && <p className="pt-2 text-sm text-ks-ink">Target bands could not be read, so no band is shown.</p>}
      </CardContent>
    </Card>
  );
}

function ImprovementAreas({ q }: { q: string }) {
  const areas = useQuery({ queryKey: statsKey("improvement-areas", q), queryFn: () => getImprovementAreas(q) });
  // CX behaviour: hidden entirely when empty or errored, never an empty strip.
  if (!areas.data || areas.data.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-ks-ink">Top improvement areas</h2>
      <ol className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {areas.data.slice(0, 12).map((area, i) => (
          <li key={area.theme_tag}>
            <Card>
              <CardContent className="flex flex-col gap-1 py-3 text-sm text-ks-ink">
                <p className="font-semibold">
                  #{i + 1} {area.theme_tag.replaceAll("_", " ")}
                </p>
                <p>
                  {formatCount(area.asset_count)} assets · {formatCount(area.section_count)} sections
                </p>
                <p>score drag {area.score_drag === null ? "–" : `−${area.score_drag.toFixed(2)}`}</p>
              </CardContent>
            </Card>
          </li>
        ))}
      </ol>
    </section>
  );
}

const COHORT_SPEC: FilterSpec = {
  name: "cohort",
  label: "Cohort",
  kind: "choice",
  options: [
    { value: "snippet", label: "By Snippet" },
    { value: "skeleton", label: "By Skeleton" },
    { value: "medium", label: "By Medium/Platform" },
    { value: "core_need", label: "By Core Need" },
    { value: "operator", label: "By Operator" },
  ],
};

const numeric = { meta: { align: "right" as const } };

const COHORT_COLUMNS: ColumnDef<CohortRow, unknown>[] = [
  { id: "cohort", header: "Cohort", accessorFn: (row) => row.display_name || row.key || "–" },
  { id: "assets", header: "Assets", accessorFn: (row) => formatCount(row.assets), ...numeric },
  { id: "rated", header: "Rated", accessorFn: (row) => formatCount(row.rated), ...numeric },
  { id: "engine", header: "Avg Marketing Engine", accessorFn: (row) => formatScore(row.avg_engine_score), ...numeric },
  {
    id: "sample",
    header: "Sample",
    cell: ({ row }) => (row.original.sample_below_min ? <Badge variant="outline">low sample</Badge> : "ok"),
  },
];

function CohortCard({ q, areaName }: { q: string; areaName: string }) {
  const controls = useControls();
  const cohort = (COHORT_SPEC.options ?? []).some((o) => o.value === controls.cohort) ? (controls.cohort as Cohort) : "snippet";
  const rows = useQuery({
    queryKey: statsKey(`cohort-${cohort}`, q),
    queryFn: () => getCohort(cohort as Exclude<Cohort, "operator">, q),
    enabled: cohort !== "operator",
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Marketing Engine Score by cohort</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <FilterBar
          layout="segmented"
          filters={[COHORT_SPEC]}
          values={{ ...controls.values, cohort }}
          onChange={controls.onChange}
        />
        {cohort === "operator" ? (
          <p className="text-sm text-ks-ink">{OPERATOR_GAP}</p>
        ) : rows.isError ? (
          <SectionError title="Cohorts" error={rows.error} areaName={areaName} />
        ) : (
          <DataTable
            columns={COHORT_COLUMNS}
            rows={rows.data ?? []}
            pending={rows.isPending}
            page={{ pageIndex: 0, pageSize: Math.max(rows.data?.length ?? 0, 1), rowCount: rows.data?.length ?? 0 }}
            onPageChange={() => {}}
            emptyMessage="No cohorts yet."
            caption="Cohorts for the filtered range"
          />
        )}
        <p className="text-sm text-ks-ink">
          Open Rate, CTR and readability per cohort are not served by CVCE yet, so those columns are not shown.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Performance (UX S2), limited to what the CVCE marketing stats router serves
 * today. Sections still to come are named at the foot, never silently dropped.
 */
export function PerformanceTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const q = statsQuery(controls);
  const [runs, formats] = useQueries({
    queries: [
      { queryKey: ["cvce_marketing", "runs-options"], queryFn: getRunOptions },
      { queryKey: ["cvce_marketing", "formats"], queryFn: getFormatOptions },
    ],
  });
  const outcomes = useQuery({ queryKey: statsKey("asset-outcomes", q), queryFn: () => getAssetOutcomes(q) });

  const runOptions = runs.data?.data.available ? runs.data.data.runs : runs.isPending ? [] : null;
  const formatOptions = formats.data?.data.available ? formats.data.data.formats : formats.isPending ? [] : null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-ks-ink">Performance</h2>
        <p className="text-sm text-ks-ink">Content-version outcomes from the authorized stats views.</p>
        {outcomes.data?.era_start && (
          <p className="text-sm text-ks-ink">Measured from the first recorded marketing asset ({outcomes.data.era_start.slice(0, 10)}).</p>
        )}
      </div>
      <PerformanceFilters controls={controls} runs={runOptions} formats={formatOptions} />
      {outcomes.isPending ? (
        <Skeleton className="h-28 w-full" />
      ) : outcomes.isError ? (
        <SectionError title="Asset outcomes" error={outcomes.error} areaName={areaName} />
      ) : outcomes.data.total_assets === 0 ? (
        <>
          <Tiles outcomes={outcomes.data} q={q} />
          <p className="text-sm text-ks-ink">
            No assets in this range. Try a wider date range, another campaign, or another medium.
          </p>
        </>
      ) : (
        <>
          <Tiles outcomes={outcomes.data} q={q} />
          <WeeklyEngineChart q={q} areaName={areaName} />
          <AssetAssessment outcomes={outcomes.data} />
          <ImprovementAreas q={q} />
          <CohortCard q={q} areaName={areaName} />
        </>
      )}
      <p className="text-sm text-ks-ink">
        Still to come on this tab: VOX adoption by medium, Individual Section Assessment, and weekly charts for VOX
        share, Rated %, Open Rate, CTR, CVR, assets published and rated sections.
      </p>
    </div>
  );
}
